// ============================================================================
// dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry.ts
//
// v5 — Ajout d'un outputGainNode par source entre merger et busDest.
//
// Chaîne complète :
//   sourceNode → gainNode → splitter → gainLeft  → analyserL → merger
//                                    → gainRight → analyserR ↗
//                                                              ↓
//                                                        outputGainNode
//                                                              ↓
//                                                           busDest (via connectRegistryOutput)
//
// gainNode    : volume utilisateur + mute utilisateur (VU-mètres restent actifs)
// outputGainNode : gain de sortie WHIP uniquement — couper ici ne stoppe pas
//                  les analyseurs → VU-mètres toujours actifs en mode "Fixe"
//
// v5.1 — rebindAudioTrack
//
// v5.2 — getMonitorNode() :
//   Expose le monitorGainNode pour que AudioMixerPipeline puisse gérer
//   explicitement la connexion à ctx.destination (connectMonitorOutput /
//   disconnectMonitorOutput), de façon symétrique à connectRegistryOutput /
//   disconnectRegistryOutput pour le busDest.
//   monitorGainNode reste connecté à ctx.destination à la création
//   (nécessaire pour maintenir le graph actif dans Chrome) mais avec
//   gain = 0.0001 (quasi-silence). La connexion réelle audible est gérée
//   via connectMonitorOutput qui monte le gain à 1.
// ============================================================================

import { getPitchWorkletUrl, PITCH_WORKLET_NAME, semitonesToPitchRatio } from './pitchShiftWorklet';
import { startAvSyncProbe, AvSyncProbe } from './avSyncProbe';
import { DIAGNOSTIC_PROBES_ENABLED } from '../../../backendServices/debugManagment/remoteLogger';
import { kWeightingCoefficients } from './loudness';

// [AV-SYNC] coûte plusieurs points de fil principal : coupée seule, les autres sondes restent actives.
const AV_SYNC_PROBE_ENABLED = false;

export interface AudioSourceHandle {
  id:         string;
  sourceNode: AudioNode;
  gainNode:   GainNode;
  ctx:        AudioContext;
  release:    () => void;
}

export interface AudioSourceAnalysers {
  analyserL: AnalyserNode;
  analyserR: AnalyserNode;
}

/**
 * La SOURCE d'un son : le flux capté, son MediaStreamAudioSourceNode et, pour un flux
 * distant, l'<audio> muet qui l'alimente. Elle est comptée : la dernière entrée qui la
 * quitte la ferme (nœud débranché, <audio> retiré, pistes micro stoppées).
 *
 * Elle est distincte de l'ENTRÉE (AudioSourceEntry, `audio-<id>`), qui porte la chaîne de
 * réglages (volume, égaliseur, effets, analyseurs, gains de sortie) : plusieurs entrées
 * peuvent s'alimenter à la même source sans en rouvrir le flux (cf. plan du 09/10/2026).
 */
interface AudioSource {
  key:        string;
  ctx:        AudioContext;
  /** Identifiant de la piste audio captée : deux flux portant la même piste sont la même source. */
  trackId:    string;
  stream:     MediaStream;
  node:       MediaStreamAudioSourceNode;
  refCount:   number;
  audioEl?:   HTMLAudioElement;
  /** Vrai pour un micro ouvert par le registre : ses pistes sont stoppées avec la source. */
  ownsTracks: boolean;
}

interface AudioSourceEntry {
  id:             string;
  ctx:            AudioContext;
  /** Source dont cette entrée tire son son (cf. AudioSource). */
  source:         AudioSource;
  /** Relais privés remis aux porteurs de poignée (cf. makeHandle). */
  taps:           GainNode[];
  sourceNode:     MediaStreamAudioSourceNode;
  /** Lowcut (passe-haut) — toujours dans la chaîne, cf. setLowcut() pour comment il
   * se "désactive" sans reconnexion du graphe. */
  filterNode:     BiquadFilterNode;
  gainNode:       GainNode;
  /** Égaliseur 3 bandes, EN SÉRIE (pas un envoi) — cf. setEqualizer(). */
  eqLow:          BiquadFilterNode;
  eqMid:          BiquadFilterNode;
  eqHigh:         BiquadFilterNode;
  gainLeft:       GainNode;
  gainRight:      GainNode;
  merger:         ChannelMergerNode;
  // ── Réverbération (send) — merger se sépare en direct/traité, remixés dans postMixNode ──
  dryGain:        GainNode;
  reverbConvolver: ConvolverNode;
  reverbWetGain:  GainNode;
  // ── Echo/delay (send) — même principe que la réverb : boucle de réinjection sur
  // delayNode lui-même pour les répétitions, feedback plafonné (cf. setEchoDelay)
  // pour ne jamais atteindre 1 — un feedback ≥1 s'auto-amplifie à l'infini (larsen).
  delayNode:       DelayNode;
  delayFeedbackGain: GainNode;
  delayWetGain:    GainNode;
  // ── Voix robot (send) — modulation en anneau : un oscillateur basse fréquence
  // pilote directement le gain (bipolaire -1..+1, cf. setRobotVoice) plutôt que
  // d'être additionné dessus — c'est ce qui donne le timbre robotique/métallique,
  // pas un simple trémolo.
  robotOscillator: OscillatorNode;
  robotRingGain:   GainNode;
  robotWetGain:    GainNode;
  // ── Chorus/double voix (send) — deux voix à délai court (bases fixes, non
  // exposées) modulées chacune par leur propre LFO à des vitesses légèrement
  // désaccordées (cf. setChorus, ×1.27 — pas un multiple simple, pour éviter tout
  // phasing périodique entre les deux) : c'est ce désaccord qui donne l'effet de
  // double voix plutôt qu'un simple vibrato.
  chorusDelayA:    DelayNode;
  chorusLfoA:      OscillatorNode;
  chorusDepthA:    GainNode;
  chorusDelayB:    DelayNode;
  chorusLfoB:      OscillatorNode;
  chorusDepthB:    GainNode;
  chorusWetGain:   GainNode;
  postMixNode:    GainNode;
  outputGainNode: GainNode;
  monitorGainNode: GainNode;
  /** Dernière cible posée par setMonitorGain (le nœud, lui, peut être en cours de rampe). */
  monitorTarget:  number;
  /** Pitch shift — null si le worklet n'était pas encore chargé à la création de
   * l'entrée (cf. ensurePitchWorkletLoaded) : setPitch() est alors un no-op pour
   * cette source, elle garde sa hauteur naturelle. */
  pitchNode:      AudioWorkletNode | null;
  splitter:       ChannelSplitterNode;
  analyserL:      AnalyserNode;
  analyserR:      AnalyserNode;
  /** Branche LUFS (pondération K → analyseurs longs), créée au premier besoin — cf. getLoudnessAnalysers(). */
  loudness?:      { nodes: AudioNode[]; analysers: AudioSourceAnalysers } | null;
  refCount:       number;
  stream:         MediaStream;
  sourceType:     'microphone' | 'stream' | 'remote' | 'screen' | 'url' | 'unknown';
}

// ─── Réponse impulsionnelle de réverbération ──────────────────────────────────
//
// Pas de fichier audio à charger : bruit blanc + enveloppe de décroissance
// exponentielle, générée à la demande et partagée par tous les ConvolverNode qui
// utilisent la même durée (un AudioBuffer n'est que de la donnée, le lire depuis
// plusieurs nœuds à la fois ne pose aucun problème). Cache par durée ARRONDIE
// (cf. DECAY_ROUND_SEC) pour rester léger pendant qu'on glisse le curseur "Durée"
// — sans ça, chaque tick régénérerait plusieurs secondes de bruit en JS.
const DEFAULT_REVERB_DECAY_SEC = 1.6;
const DECAY_ROUND_SEC = 0.1;

let cachedImpulseCtx: AudioContext | null = null;
const cachedImpulseByDuration = new Map<number, AudioBuffer>();

function getReverbImpulse(ctx: AudioContext, durationSec: number = DEFAULT_REVERB_DECAY_SEC): AudioBuffer {
  if (cachedImpulseCtx !== ctx) {
    cachedImpulseCtx = ctx;
    cachedImpulseByDuration.clear();
  }
  const rounded = Math.round(Math.max(0.2, Math.min(4, durationSec)) / DECAY_ROUND_SEC) * DECAY_ROUND_SEC;
  const cached = cachedImpulseByDuration.get(rounded);
  if (cached) return cached;

  const decay  = 2.5;
  const length = Math.floor(ctx.sampleRate * rounded);
  const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let ch = 0; ch < impulse.numberOfChannels; ch++) {
    const data = impulse.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
    }
  }
  cachedImpulseByDuration.set(rounded, impulse);
  return impulse;
}

// Lowcut "désactivé" = fréquence de coupure plancher (sous l'audible) plutôt que de
// débrancher le nœud — évite toute reconnexion du graphe en direct pour un simple
// on/off. 20 Hz est la limite basse standard de l'audition humaine.
const LOWCUT_OFF_HZ = 20;

// ─── Constantes analyseur ─────────────────────────────────────────────────────

const FFT_SIZE     = 2048;   // avant 256;
const SMOOTHING    = 0.15;
const MIN_DECIBELS = -70;
const MAX_DECIBELS = -10;

function createAnalyserNode(ctx: AudioContext): AnalyserNode {
  const a = ctx.createAnalyser();
  a.fftSize               = FFT_SIZE;
  a.smoothingTimeConstant = SMOOTHING;
  a.minDecibels           = MIN_DECIBELS;
  a.maxDecibels           = MAX_DECIBELS;
  return a;
}

// ─── Classe principale ────────────────────────────────────────────────────────

export class AudioSourceRegistry {

  private ctx: AudioContext | null = null;
  private entries = new Map<string, AudioSourceEntry>();

  // Compteur monotone par id, incrémenté à CHAQUE création d'entrée (pas au
  // réemprunt). Il survit à release()/forceRelease() : c'est le seul moyen pour
  // un consommateur de détecter qu'une entrée a été détruite puis recréée sous
  // le même id — les nœuds de gain sont alors neufs et tous les réglages
  // appliqués précédemment (gain, monitor) sont perdus. Cf. HlsAudioPanel, qui
  // s'en sert pour réappliquer son fader après une réacquisition du flux retour.
  private generations = new Map<string, number>();

  // Entrées dont l'écoute est coupée d'office (cf. suppressMonitor). Indexé par id,
  // comme `generations` : une entrée recréée sous le même id naît déjà coupée.
  private monitorSuppressed = new Set<string>();

  // Sources actives, par clé. Étape 1 du plan : une source par entrée (clé = id de
  // l'entrée), donc comportement inchangé ; le partage vient aux étapes suivantes.
  private sources = new Map<string, AudioSource>();

  // ── Initialisation ────────────────────────────────────────────────────────

  init(): void {
    if (this.ctx && this.ctx.state !== 'closed') return;
    this.ctx = new AudioContext();
    console.log('[AudioSourceRegistry] init() — nouveau AudioContext créé');
  }

  setContext(ctx: AudioContext): void {
    this.ctx = ctx;
    console.log('[AudioSourceRegistry] setContext() — AudioContext externe injecté');
  }

  getContext(): AudioContext {
    if (!this.ctx || this.ctx.state === 'closed') this.init();
    this.ensurePitchWorkletLoaded();
    return this.ctx!;
  }

  // ── Worklet pitch shift — chargé une fois par AudioContext ─────────────────
  //
  // addModule() est asynchrone ; borrowFromStream() ne l'est PAS (trop d'appelants
  // synchrones existants pour changer ça sans risque). Le nœud pitch n'est donc créé
  // que pour les sources empruntées APRÈS la fin du chargement — dans la pratique le
  // module (un Blob en mémoire, pas de round-trip réseau) est prêt bien avant la
  // première source réelle. Si jamais une source arrive trop tôt, elle garde
  // simplement sa hauteur naturelle (pitchNode = null) plutôt que de bloquer.
  private pitchWorkletCtx   : AudioContext | null = null;
  private pitchWorkletReady = false;

  private ensurePitchWorkletLoaded(): void {
    const ctx = this.ctx;
    if (!ctx || this.pitchWorkletCtx === ctx) return;
    this.pitchWorkletCtx   = ctx;
    this.pitchWorkletReady = false;
    if (!ctx.audioWorklet) {
      console.warn('[AudioSourceRegistry] AudioWorklet indisponible sur ce navigateur — effet Pitch désactivé');
      return;
    }
    ctx.audioWorklet.addModule(getPitchWorkletUrl())
      .then(() => {
        if (this.pitchWorkletCtx === ctx) {
          this.pitchWorkletReady = true;
          console.log('[AudioSourceRegistry] worklet pitch chargé');
        }
      })
      .catch(err => console.warn('[AudioSourceRegistry] échec chargement worklet pitch — effet indisponible', err));
  }

  async resume(): Promise<void> {
    if (this.ctx?.state === 'suspended') {
      await this.ctx.resume();
      console.log('[AudioSourceRegistry] resume()');
    }
  }

  // ── Enregistrement d'une source stream ────────────────────────────────────

  borrowFromStream(
    id:         string,
    stream:     MediaStream,
    sourceType: AudioSourceEntry['sourceType'] = 'unknown',
    useAudioEl: boolean = false,
    /** Clé de la source à partager ; par défaut l'id de l'entrée (aucun partage). */
    sourceKey?: string,
  ): AudioSourceHandle {
	  
 
	  
	  
    const ctx = this.getContext();

    const existing = this.entries.get(id);
    if (existing) {
      existing.refCount++;
      console.log(`[AudioSourceRegistry] borrowFromStream(${id}) — réutilisation, refCount=${existing.refCount}`);
      return this.makeHandle(existing);
    }

    const audioTracks = stream.getAudioTracks();
	
console.log('[ASR] borrowFromStream', id, 
  'tracks=', audioTracks.map(t => `${t.label}:${t.readyState}:muted=${t.muted}`).join(','),
  'ctxState=', ctx.state);	 	
	
	
	
    if (!audioTracks.length) {
      console.warn(`[AudioSourceRegistry] borrowFromStream(${id}) — AUCUNE piste audio !`);
      throw new Error(`[AudioSourceRegistry] stream ${id} sans piste audio`);
    }

    console.log(`[AudioSourceRegistry] borrowFromStream(${id}) — ${audioTracks.length} piste(s), type=${sourceType}`);
    audioTracks.forEach((t, i) => {
      console.log(`[AudioSourceRegistry]   track[${i}] label="${t.label}" enabled=${t.enabled} muted=${t.muted}`);
    });

    // Étape 3 : sans clé explicite (micro), la source est identifiée par la piste captée —
    // un même flux emprunté par plusieurs entrées (slot, redirection du pupitre, source
    // fixe, Mixeur…) n'ouvre qu'un seul MediaStreamSource.
    const source     = this.acquireSource(sourceKey ?? `trk:${audioTracks[0].id}`, ctx, stream, useAudioEl, sourceType === 'microphone');
    const sourceNode = source.node;

    const filterNode         = ctx.createBiquadFilter();
    filterNode.type          = 'highpass';
    filterNode.frequency.value = LOWCUT_OFF_HZ; // désactivé par défaut, cf. setLowcut()
    filterNode.Q.value       = 0.7;             // pente douce, pas de bosse de résonance

    const gainNode       = ctx.createGain();
    gainNode.gain.value  = 1;

    // ── Égaliseur 3 bandes — EN SÉRIE (pas un envoi comme reverb/écho/chorus : une
    // EQ doit filtrer le signal qui passe, pas en mélanger une copie). Neutre à 0dB
    // sur les 3 bandes par défaut, même principe que LOWCUT_OFF_HZ : toujours dans
    // le graphe, jamais reconnecté en direct pour un simple on/off (cf. setEqualizer).
    const eqLow              = ctx.createBiquadFilter();
    eqLow.type                = 'lowshelf';
    eqLow.frequency.value     = 150;
    eqLow.gain.value          = 0;
    const eqMid               = ctx.createBiquadFilter();
    eqMid.type                 = 'peaking';
    eqMid.frequency.value      = 1000;
    eqMid.Q.value              = 1;
    eqMid.gain.value           = 0;
    const eqHigh               = ctx.createBiquadFilter();
    eqHigh.type                 = 'highshelf';
    eqHigh.frequency.value      = 4000;
    eqHigh.gain.value           = 0;
    gainNode.connect(eqLow);
    eqLow.connect(eqMid);
    eqMid.connect(eqHigh);

    const splitter       = ctx.createChannelSplitter(2);
    const gainLeft       = ctx.createGain();
    const gainRight      = ctx.createGain();
    gainLeft.gain.value  = 1;
    gainRight.gain.value = 1;

    const analyserL      = createAnalyserNode(ctx);
    const analyserR      = createAnalyserNode(ctx);
    const merger         = ctx.createChannelMerger(2);

    // ── Réverbération (send) ── dry toujours à 1 (le signal sec n'est jamais réduit),
    // wet à 0 par défaut (désactivée) — cf. setReverb().
    const dryGain             = ctx.createGain();
    dryGain.gain.value        = 1;
    const reverbConvolver     = ctx.createConvolver();
    reverbConvolver.buffer    = getReverbImpulse(ctx);
    reverbConvolver.normalize = true;
    const reverbWetGain       = ctx.createGain();
    reverbWetGain.gain.value  = 0;

    // ── Echo/delay (send) ── boucle delayNode → delayFeedbackGain → delayNode pour
    // les répétitions ; wet à 0 par défaut (désactivé) — cf. setEchoDelay().
    const delayNode              = ctx.createDelay(2.0); // 2s de plafond, largement assez pour un écho voix
    delayNode.delayTime.value    = 0.3;
    const delayFeedbackGain      = ctx.createGain();
    delayFeedbackGain.gain.value = 0;
    const delayWetGain           = ctx.createGain();
    delayWetGain.gain.value      = 0;

    // ── Voix robot (send) ── modulation en anneau, wet à 0 par défaut (désactivée)
    // — cf. setRobotVoice(). robotRingGain.gain reste à 0 : c'est l'oscillateur
    // connecté SUR le paramètre (pas sur le signal) qui pilote tout le gain,
    // bipolaire -1..+1 — une vraie modulation en anneau, pas un trémolo unipolaire.
    const robotOscillator        = ctx.createOscillator();
    robotOscillator.type         = 'sine';
    robotOscillator.frequency.value = 35;
    const robotRingGain          = ctx.createGain();
    robotRingGain.gain.value     = 0;
    robotOscillator.connect(robotRingGain.gain);
    robotOscillator.start();
    const robotWetGain           = ctx.createGain();
    robotWetGain.gain.value      = 0;

    // ── Chorus/double voix (send) ── deux voix à délai court modulé, wet à 0 par
    // défaut (désactivé) — cf. setChorus(). Bases de délai différentes (22/30ms)
    // pour que les deux voix ne se recouvrent jamais exactement.
    const chorusDelayA           = ctx.createDelay(0.05);
    chorusDelayA.delayTime.value = 0.022;
    const chorusLfoA             = ctx.createOscillator();
    chorusLfoA.type              = 'sine';
    chorusLfoA.frequency.value   = 1.1;
    const chorusDepthA           = ctx.createGain();
    chorusDepthA.gain.value      = 0;
    chorusLfoA.connect(chorusDepthA);
    chorusDepthA.connect(chorusDelayA.delayTime);
    chorusLfoA.start();

    const chorusDelayB           = ctx.createDelay(0.05);
    chorusDelayB.delayTime.value = 0.030;
    const chorusLfoB             = ctx.createOscillator();
    chorusLfoB.type              = 'sine';
    chorusLfoB.frequency.value   = 1.1 * 1.27; // désaccordé — pas un multiple simple, évite le phasing périodique
    const chorusDepthB           = ctx.createGain();
    chorusDepthB.gain.value      = 0;
    chorusLfoB.connect(chorusDepthB);
    chorusDepthB.connect(chorusDelayB.delayTime);
    chorusLfoB.start();

    const chorusWetGain          = ctx.createGain();
    chorusWetGain.gain.value     = 0;

    const postMixNode         = ctx.createGain();
    postMixNode.gain.value    = 1;

    const outputGainNode       = ctx.createGain();
    outputGainNode.gain.value  = 1;

    // monitorGainNode : gain à 0.0001 pour maintenir le graph actif dans Chrome
    // sans être audible. connectMonitorOutput() monte le gain à 1 sur la source
    // voulue, disconnectMonitorOutput() le remet à 0.0001.
    const monitorGainNode      = ctx.createGain();
    monitorGainNode.gain.value = this.monitorSuppressed.has(id) ? 0 : 0.0001;

    sourceNode.connect(filterNode);
    filterNode.connect(gainNode);

    // Pitch shift — inséré entre gainNode et le reste de la chaîne quand le worklet
    // est prêt (cf. ensurePitchWorkletLoaded), neutre par défaut (pitchRatio=1,
    // cf. defaultValue du paramètre) exactement comme LOWCUT_OFF_HZ pour le lowcut :
    // toujours dans le graphe, jamais reconnecté en direct pour un simple on/off.
    let pitchNode: AudioWorkletNode | null = null;
    if (this.pitchWorkletReady) {
      try {
        pitchNode = new AudioWorkletNode(ctx, PITCH_WORKLET_NAME);
      } catch (err) {
        console.warn(`[AudioSourceRegistry] borrowFromStream(${id}) — création du nœud pitch échouée`, err);
      }
    }
    const preSplit: AudioNode = pitchNode ?? eqHigh;
    if (pitchNode) eqHigh.connect(pitchNode);

    preSplit.connect(splitter);
    try {
      splitter.connect(gainLeft,  0);
      splitter.connect(gainRight, 1);
    } catch {
      preSplit.connect(gainLeft);
      preSplit.connect(gainRight);
    }

    gainLeft.connect(analyserL);
    gainRight.connect(analyserR);
    analyserL.connect(merger, 0, 0);
    analyserR.connect(merger, 0, 1);

    merger.connect(dryGain);
    dryGain.connect(postMixNode);
    merger.connect(reverbConvolver);
    reverbConvolver.connect(reverbWetGain);
    reverbWetGain.connect(postMixNode);

    merger.connect(delayNode);
    delayNode.connect(delayFeedbackGain);
    delayFeedbackGain.connect(delayNode); // réinjection — répétitions successives
    delayNode.connect(delayWetGain);
    delayWetGain.connect(postMixNode);

    merger.connect(robotRingGain);
    robotRingGain.connect(robotWetGain);
    robotWetGain.connect(postMixNode);

    merger.connect(chorusDelayA);
    chorusDelayA.connect(chorusWetGain);
    merger.connect(chorusDelayB);
    chorusDelayB.connect(chorusWetGain);
    chorusWetGain.connect(postMixNode);

    postMixNode.connect(outputGainNode);
    postMixNode.connect(monitorGainNode);
    monitorGainNode.connect(ctx.destination); // ← maintenu pour garder le graph actif

    const entry: AudioSourceEntry = {
      id, ctx, source, taps: [], sourceNode, filterNode, gainNode, eqLow, eqMid, eqHigh, gainLeft, gainRight,
      merger, dryGain, reverbConvolver, reverbWetGain,
      delayNode, delayFeedbackGain, delayWetGain,
      robotOscillator, robotRingGain, robotWetGain,
      chorusDelayA, chorusLfoA, chorusDepthA,
      chorusDelayB, chorusLfoB, chorusDepthB, chorusWetGain, postMixNode,
      outputGainNode, monitorGainNode, monitorTarget: 0.0001, pitchNode, splitter, analyserL, analyserR,
      refCount: 1, stream, sourceType,
    };
    this.entries.set(id, entry);
    this.generations.set(id, (this.generations.get(id) ?? 0) + 1);

    console.log(`[AudioSourceRegistry] borrowFromStream(${id}) — OK, refCount=1, ctxState=${ctx.state}`);
    this.probeSignal(entry);
    // 🔍 DIAGNOSTIC 2026-10-20 — cf. avSyncProbe.ts.
    const avProbe = DIAGNOSTIC_PROBES_ENABLED && AV_SYNC_PROBE_ENABLED ? startAvSyncProbe(id, ctx, sourceNode, stream) : null;
    if (avProbe) this.avSyncProbes.set(id, avProbe);
    return this.makeHandle(entry);
  }

  // ── Emprunt micro ─────────────────────────────────────────────────────────

  // Diagnostic (16/10/2026) : une ligne console.warn (donc remontée aux journaux distants)
  // par entrée créée, 2 s plus tard. Crête = maximum mesuré sur l'analyseur (avant tous
  // les gains) pendant 0,5 s, pour l'entrée ET pour les autres entrées de la même source :
  // toutes à 0 = la source est muette (lecture arrêtée, média sans son) ; seule la
  // nouvelle à 0 = c'est le partage qui coupe. `partage` = nombre d'entrées sur la source.
  private probeSignal(entry: AudioSourceEntry): void {
    if (!DIAGNOSTIC_PROBES_ENABLED) return;
    setTimeout(() => {
      try {
        if (this.entries.get(entry.id) !== entry) return;
        const group = Array.from(this.entries.values()).filter(e => e.source === entry.source);
        const peaks = new Map<string, number>(group.map(e => [e.id, 0]));
        const buf = new Float32Array(entry.analyserL.fftSize);
        let n = 0;
        const sample = () => {
          group.forEach(e => {
            try {
              e.analyserL.getFloatTimeDomainData(buf);
              let m = peaks.get(e.id) ?? 0;
              for (let i = 0; i < buf.length; i++) m = Math.max(m, Math.abs(buf[i]));
              peaks.set(e.id, m);
            } catch {}
          });
          if (++n < 10) { setTimeout(sample, 50); return; }
          const t = entry.stream.getAudioTracks()[0];
          const others = group.filter(e => e !== entry)
            .map(e => `${e.id.slice(0, 22)}=${(peaks.get(e.id) ?? 0).toFixed(4)}`).join(' ');
          console.warn(
            `[ASR-PROBE] ${entry.id} type=${entry.sourceType} ctx=${entry.ctx.state}` +
            ` piste=${t ? `${t.readyState},muted=${t.muted},enabled=${t.enabled}` : 'aucune'}` +
            ` crete=${(peaks.get(entry.id) ?? 0).toFixed(4)} partage=${group.length}` +
            ` source=${entry.source.key.slice(0, 12)} refSource=${entry.source.refCount}` +
            (others ? ` | autres: ${others}` : '')
          );
        };
        sample();
      } catch (e) {
        console.warn(`[ASR-PROBE] ${entry.id} sonde en échec`, e);
      }
    }, 2000);
  }

  // Ouvertures de micro en cours, par clé : deux demandes simultanées du même micro
  // n'ouvrent qu'un seul getUserMedia.
  private micPending = new Map<string, Promise<MediaStream>>();

  // 🔍 DIAGNOSTIC 2026-10-20 — sondes son/image par entrée, cf. avSyncProbe.ts.
  private avSyncProbes = new Map<string, AvSyncProbe>();

  async borrowFromMicrophone(id: string, deviceId?: string): Promise<AudioSourceHandle> {
    const existing = this.entries.get(id);
    if (existing) {
      existing.refCount++;
      console.log(`[AudioSourceRegistry] borrowFromMicrophone(${id}) — réutilisation, refCount=${existing.refCount}`);
      return this.makeHandle(existing);
    }

    await this.resume();

    // Un micro est ouvert UNE fois par périphérique, quel que soit le nombre d'entrées
    // qui l'empruntent (slot caméra, source fixe, Mixeur…).
    const key = `mic:${deviceId ?? 'default'}`;
    const shared = this.sources.get(key);
    if (shared && shared.stream.getAudioTracks().some(t => t.readyState === 'live')) {
      console.log(`[AudioSourceRegistry] borrowFromMicrophone(${id}) — micro ${key} déjà ouvert, partagé`);
      return this.borrowFromStream(id, shared.stream, 'microphone', false, key);
    }
    if (shared) this.sources.delete(key); // pistes mortes (périphérique débranché) : on rouvre

    let pending = this.micPending.get(key);
    if (!pending) {
      pending = this.openMicrophone(key, deviceId).finally(() => this.micPending.delete(key));
      this.micPending.set(key, pending);
    }
    const stream = await pending;
    return this.borrowFromStream(id, stream, 'microphone', false, key);
  }

  private async openMicrophone(key: string, deviceId?: string): Promise<MediaStream> {
    const constraints: MediaStreamConstraints = {
      audio: deviceId
        ? { deviceId: { exact: deviceId }, echoCancellation: false, noiseSuppression: false, autoGainControl: false }
        : { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      video: false,
    };

    console.log(`[AudioSourceRegistry] ouverture du micro ${key}`);
    return navigator.mediaDevices.getUserMedia(constraints);
  }

  // ── Rebind du sourceNode après renouvellement de track ────────────────────

  rebindAudioTrack(id: string, stream: MediaStream): void {
    const entry = this.entries.get(id);
    if (!entry) {
      console.warn(`[AudioSourceRegistry] rebindAudioTrack(${id}) — entrée introuvable`);
      return;
    }

    const newAudioTracks = stream.getAudioTracks();
    if (!newAudioTracks.length) {
      console.warn(`[AudioSourceRegistry] rebindAudioTrack(${id}) — aucune piste audio dans le stream`);
      return;
    }

    console.log(
      `[AudioSourceRegistry] rebindAudioTrack(${id}) — remplacement sourceNode` +
      ` track="${newAudioTracks[0].label}" readyState=${newAudioTracks[0].readyState}`
    );

    const source = entry.source;
    const oldNode = source.node;
    const singleTrackStream = new MediaStream([newAudioTracks[0]]);
    const newSourceNode = entry.ctx.createMediaStreamSource(singleTrackStream);

    // La source est partagée : TOUTES les entrées qu'elle alimente suivent la nouvelle
    // piste, pas seulement celle qu'on rebranche. Par filterNode, pas directement
    // gainNode — sinon un rebind après renouvellement de track shunte le lowcut, qui
    // redeviendrait injoignable jusqu'à la prochaine reconnexion.
    this.entries.forEach(other => {
      if (other.source !== source) return;
      try { oldNode.disconnect(other.filterNode); } catch {}
      newSourceNode.connect(other.filterNode);
      other.taps.forEach(t => {
        try { oldNode.disconnect(t); } catch {}
        newSourceNode.connect(t);
      });
      other.sourceNode = newSourceNode;
      other.stream     = stream;
    });
    try { oldNode.disconnect(); } catch {}

    source.node    = newSourceNode;
    source.stream  = stream;
    source.trackId = newAudioTracks[0].id;
    // Une source identifiée par sa piste change de clé avec elle.
    if (source.key.startsWith('trk:') && this.sources.get(source.key) === source) {
      this.sources.delete(source.key);
      source.key = `trk:${source.trackId}`;
      this.sources.set(source.key, source);
    }

    console.log(`[AudioSourceRegistry] rebindAudioTrack(${id}) — OK, chaîne audio intacte`);
  }

  // ── Contrôle volume / mute ────────────────────────────────────────────────

  setMuted(id: string, muted: boolean): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    const target = muted ? 0 : 1;
    entry.gainNode.gain.setTargetAtTime(target, entry.ctx.currentTime, 0.01);
    console.log(`[AudioSourceRegistry] setMuted(${id}, ${muted})`);
  }

  setGain(id: string, gain: number): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    const clamped = Math.max(0, Math.min(2, gain));
    entry.gainNode.gain.setTargetAtTime(clamped, entry.ctx.currentTime, 0.01);
  }

  /**
   * Gain linéaire appliqué en ce moment par le gainNode, AVANT les analyseurs
   * (2026-10-23) : un VU qui rejoue un fader en déduit la part déjà présente dans
   * le signal mesuré, pour ne pas la compter deux fois. 1 si l'entrée n'existe pas.
   */
  getGain(id: string): number {
    const entry = this.entries.get(id);
    return entry ? entry.gainNode.gain.value : 1;
  }

  setOutputGain(id: string, gain: number): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.outputGainNode.gain.setTargetAtTime(gain, entry.ctx.currentTime, 0.02);
    console.log(`[AudioSourceRegistry] setOutputGain(${id}, ${gain})`);
  }

  setMonitorGain(id: string, gain: number): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.monitorTarget = gain;
    const applied = this.monitorSuppressed.has(id) ? 0 : gain;
    entry.monitorGainNode.gain.setTargetAtTime(applied, entry.ctx.currentTime, 0.02);
    console.log(`[AudioSourceRegistry] setMonitorGain(${id}, ${gain})${applied !== gain ? ' — écoute coupée, reste à 0' : ''}`);
  }

  /**
   * Coupe l'écoute d'une entrée quel que soit le gain demandé par ailleurs (son du flux
   * final, Switch audio, Mix…) : setMonitorGain mémorise toujours sa cible, mais le nœud
   * reste à 0 tant que la coupure tient. Levée, la dernière cible est rétablie.
   *
   * Sert à l'écoute du bloc Mixeur du deck (useDeckMixMonitor) : elle reprend l'écoute
   * locale des sources du deck par ses propres entrées, sans quoi chaque source
   * s'entendrait deux fois. Indexé par id : survit à la recréation de l'entrée.
   */
  suppressMonitor(id: string, suppressed: boolean): void {
    if (suppressed === this.monitorSuppressed.has(id)) return;
    if (suppressed) this.monitorSuppressed.add(id); else this.monitorSuppressed.delete(id);
    const entry = this.entries.get(id);
    if (!entry) return;
    const applied = suppressed ? 0 : entry.monitorTarget;
    entry.monitorGainNode.gain.setTargetAtTime(applied, entry.ctx.currentTime, 0.02);
    console.log(`[AudioSourceRegistry] suppressMonitor(${id}, ${suppressed}) → écoute ${applied}`);
  }

  /**
   * Diagnostic : énergie du signal APRÈS les effets (postMixNode, avant les gains de
   * sortie et d'écoute), mesurée 0,3 s. Compare « filtre actif » et « filtre coupé » :
   * si le RMS ne bouge pas, l'effet n'atteint pas la sortie ; s'il bouge, il est dans
   * le signal et la question devient son niveau d'écoute.
   */
  measurePostMix(id: string): Promise<{ peak: number; rms: number } | null> {
    const e = this.entries.get(id);
    if (!e) return Promise.resolve(null);
    return new Promise(resolve => {
      const an = e.ctx.createAnalyser();
      an.fftSize = 2048;
      e.postMixNode.connect(an);
      const buf = new Float32Array(an.fftSize);
      let peak = 0, sum = 0, count = 0, n = 0;
      const sample = () => {
        an.getFloatTimeDomainData(buf);
        for (let i = 0; i < buf.length; i++) {
          const a = Math.abs(buf[i]);
          if (a > peak) peak = a;
          sum += buf[i] * buf[i];
        }
        count += buf.length;
        if (++n < 6) { setTimeout(sample, 50); return; }
        try { e.postMixNode.disconnect(an); } catch {}
        resolve({ peak: Math.round(peak * 10000) / 10000, rms: Math.round(Math.sqrt(sum / count) * 10000) / 10000 });
      };
      sample();
    });
  }

  /** Instantané des réglages réels d'une entrée (diagnostic : ce que les nœuds portent VRAIMENT). */
  describeEntry(id: string): Record<string, number> | null {
    const e = this.entries.get(id);
    if (!e) return null;
    const v = (n: GainNode) => Math.round(n.gain.value * 1000) / 1000;
    return {
      gain: v(e.gainNode), gainL: v(e.gainLeft), gainR: v(e.gainRight),
      monitor: v(e.monitorGainNode), monitorCible: e.monitorTarget, sortie: v(e.outputGainNode),
      echoMix: v(e.delayWetGain), echoRetour: v(e.delayFeedbackGain),
      reverbMix: v(e.reverbWetGain), robotMix: v(e.robotWetGain), choeurMix: v(e.chorusWetGain),
      eqBas: e.eqLow.gain.value, eqMed: e.eqMid.gain.value, eqHaut: e.eqHigh.gain.value,
    };
  }

  /** Dernier gain d'écoute demandé pour l'entrée (0.0001 à la création), ou null si absente. */
  getMonitorGain(id: string): number | null {
    return this.entries.get(id)?.monitorTarget ?? null;
  }

  setGainLeft(id: string, gain: number): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.gainLeft.gain.setTargetAtTime(Math.max(0, Math.min(2, gain)), entry.ctx.currentTime, 0.01);
  }

  setGainRight(id: string, gain: number): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.gainRight.gain.setTargetAtTime(Math.max(0, Math.min(2, gain)), entry.ctx.currentTime, 0.01);
  }

  // ── Effets audio ───────────────────────────────────────────────────────────

  /** Low-cut (passe-haut). `frequencyHz` ignoré si `enabled` est faux — le nœud
   * retombe simplement sur LOWCUT_OFF_HZ, jamais débranché. */
  setLowcut(id: string, enabled: boolean, frequencyHz: number = 80): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    const target = enabled ? Math.max(20, Math.min(500, frequencyHz)) : LOWCUT_OFF_HZ;
    entry.filterNode.frequency.setTargetAtTime(target, entry.ctx.currentTime, 0.02);
  }

  /** Réverbération (send). `mix` : proportion de signal traité ajouté au signal sec,
   * 0-1 — le sec n'est jamais réduit (cf. dryGain, toujours à 1). `decaySec` : durée
   * de la traîne (taille de pièce perçue) — régénère l'impulsion si besoin, mais
   * reste sur le cache par durée arrondie (cf. getReverbImpulse) le reste du temps. */
  setReverb(id: string, enabled: boolean, mix: number = 0.25, decaySec: number = DEFAULT_REVERB_DECAY_SEC): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.reverbConvolver.buffer = getReverbImpulse(entry.ctx, decaySec);
    const target = enabled ? Math.max(0, Math.min(1, mix)) : 0;
    entry.reverbWetGain.gain.setTargetAtTime(target, entry.ctx.currentTime, 0.05);
  }

  /** Echo/delay. `feedback` plafonné à 0.8 quoi qu'il arrive — un feedback ≥1 sur une
   * boucle de réinjection s'auto-amplifie à l'infini (larsen), jamais souhaitable en direct. */
  setEchoDelay(id: string, enabled: boolean, delayMs: number = 300, feedback: number = 0.35, mix: number = 0.3): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    const targetDelay    = Math.max(0.01, Math.min(2, delayMs / 1000));
    const targetFeedback = enabled ? Math.max(0, Math.min(0.8, feedback)) : 0;
    const targetMix       = enabled ? Math.max(0, Math.min(1, mix)) : 0;
    entry.delayNode.delayTime.setTargetAtTime(targetDelay, entry.ctx.currentTime, 0.02);
    entry.delayFeedbackGain.gain.setTargetAtTime(targetFeedback, entry.ctx.currentTime, 0.05);
    entry.delayWetGain.gain.setTargetAtTime(targetMix, entry.ctx.currentTime, 0.05);
  }

  /** Voix robot (modulation en anneau). `freqHz` : fréquence du porteur — grave
   * (20-40Hz) pour le robot classique, plus haut pour un timbre plus métallique.
   * `mix` : proportion de signal modulé mélangé au signal sec, 0-1. */
  setRobotVoice(id: string, enabled: boolean, freqHz: number = 35, mix: number = 1): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    const targetFreq = Math.max(10, Math.min(500, freqHz));
    entry.robotOscillator.frequency.setTargetAtTime(targetFreq, entry.ctx.currentTime, 0.02);
    const target = enabled ? Math.max(0, Math.min(1, mix)) : 0;
    entry.robotWetGain.gain.setTargetAtTime(target, entry.ctx.currentTime, 0.05);
  }

  /** Chorus/double voix. `rateHz` : vitesse du vibrato des deux voix (la seconde
   * suit à ×1.27, cf. construction de l'entrée). `depthMs` : amplitude de la
   * modulation du délai, plafonnée pour ne jamais rendre un délai négatif (bases
   * 22/30ms). `mix` : proportion de signal chorusé mélangé au sec, 0-1. */
  setChorus(id: string, enabled: boolean, rateHz: number = 1.1, depthMs: number = 4, mix: number = 0.4): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    const targetRate  = Math.max(0.05, Math.min(8, rateHz));
    const targetDepth = Math.max(0, Math.min(0.01, depthMs / 1000)); // 10ms max, sous les bases 22/30ms
    entry.chorusLfoA.frequency.setTargetAtTime(targetRate, entry.ctx.currentTime, 0.05);
    entry.chorusLfoB.frequency.setTargetAtTime(targetRate * 1.27, entry.ctx.currentTime, 0.05);
    entry.chorusDepthA.gain.setTargetAtTime(targetDepth, entry.ctx.currentTime, 0.05);
    entry.chorusDepthB.gain.setTargetAtTime(targetDepth, entry.ctx.currentTime, 0.05);
    const target = enabled ? Math.max(0, Math.min(1, mix)) : 0;
    entry.chorusWetGain.gain.setTargetAtTime(target, entry.ctx.currentTime, 0.05);
  }

  /** Égaliseur 3 bandes (dB, -15..+15). EN SÉRIE, pas un envoi : désactivé = les
   * 3 bandes reviennent à 0dB (transparent), le signal n'est jamais court-circuité. */
  setEqualizer(id: string, enabled: boolean, lowDb: number = 0, midDb: number = 0, highDb: number = 0): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    const clamp = (v: number) => Math.max(-15, Math.min(15, v));
    const now = entry.ctx.currentTime;
    entry.eqLow.gain.setTargetAtTime(enabled ? clamp(lowDb) : 0, now, 0.02);
    entry.eqMid.gain.setTargetAtTime(enabled ? clamp(midDb) : 0, now, 0.02);
    entry.eqHigh.gain.setTargetAtTime(enabled ? clamp(highDb) : 0, now, 0.02);
  }

  /** Pitch shift (demi-tons, -12..+12). No-op silencieux si le worklet n'était pas
   * encore prêt quand cette source a été empruntée (cf. pitchNode, entry.pitchNode). */
  setPitch(id: string, enabled: boolean, semitones: number = 0): void {
    const entry = this.entries.get(id);
    if (!entry || !entry.pitchNode) return;
    const param = entry.pitchNode.parameters.get('pitchRatio');
    if (!param) return;
    const target = enabled ? semitonesToPitchRatio(Math.max(-12, Math.min(12, semitones))) : 1;
    param.setTargetAtTime(target, entry.ctx.currentTime, 0.02);
  }

  // ── Lecture des analyseurs ────────────────────────────────────────────────

  getAnalysers(id: string): AudioSourceAnalysers | null {
    const entry = this.entries.get(id);
    if (!entry) return null;
    return { analyserL: entry.analyserL, analyserR: entry.analyserR };
  }

  /**
   * Analyseurs du signal pondéré K (ITU-R BS.1770) pour le mode LUFS des VU-mètres
   * (2026-10-23). Branche prise au même point que les analyseurs crête (après volume,
   * égaliseur et gains L/R), créée au premier appel seulement : le mode crête ne paie
   * rien. Tampon de 32768 échantillons, dont on lit les 400 ms du LUFS Momentary.
   * null si le navigateur n'a pas d'IIRFilterNode.
   */
  getLoudnessAnalysers(id: string): AudioSourceAnalysers | null {
    const entry = this.entries.get(id);
    if (!entry) return null;
    if (entry.loudness !== undefined) return entry.loudness?.analysers ?? null;
    const ctx = entry.ctx;
    if (typeof ctx.createIIRFilter !== 'function') { entry.loudness = null; return null; }

    const [shelf, highpass] = kWeightingCoefficients(ctx.sampleRate);
    // Puits muet : relie la branche au graphe (certains navigateurs ne font tourner
    // un analyseur que s'il mène à la destination) sans rien ajouter au son.
    const sink = ctx.createGain();
    sink.gain.value = 0;
    sink.connect(entry.merger, 0, 0);
    const nodes: AudioNode[] = [sink];
    const branch = (from: GainNode): AnalyserNode => {
      const s = ctx.createIIRFilter(shelf.feedforward, shelf.feedback);
      const h = ctx.createIIRFilter(highpass.feedforward, highpass.feedback);
      const a = ctx.createAnalyser();
      a.fftSize = 32768;
      from.connect(s); s.connect(h); h.connect(a); a.connect(sink);
      nodes.push(s, h, a);
      return a;
    };
    const analysers = { analyserL: branch(entry.gainLeft), analyserR: branch(entry.gainRight) };
    entry.loudness = { nodes, analysers };
    return analysers;
  }

  getMerger(id: string): GainNode | null {
    return this.entries.get(id)?.outputGainNode ?? null;
  }

  // ── v5.2 : exposition du monitorGainNode ─────────────────────────────────
  getMonitorNode(id: string): GainNode | null {
    return this.entries.get(id)?.monitorGainNode ?? null;
  }

  createAnalyser(id: string): AudioSourceAnalysers | null {
    const entry = this.entries.get(id);
    if (!entry) return null;
    const ctx       = entry.ctx;
    const analyserL = createAnalyserNode(ctx);
    const analyserR = createAnalyserNode(ctx);
    try {
      entry.splitter.connect(analyserL, 0);
      entry.splitter.connect(analyserR, 1);
    } catch {
      entry.gainNode.connect(analyserL);
      entry.gainNode.connect(analyserR);
    }
    return { analyserL, analyserR };
  }

  // ── Lecture de l'état ─────────────────────────────────────────────────────

  has(id: string): boolean                      { return this.entries.has(id); }
  /** Numéro de création de l'entrée courante (0 si jamais créée) — cf. `generations`. */
  getGeneration(id: string): number             { return this.generations.get(id) ?? 0; }
  getSourceNode(id: string): AudioNode | null   { return this.entries.get(id)?.sourceNode ?? null; }
  getStream(id: string): MediaStream | null     { return this.entries.get(id)?.stream ?? null; }
  getAllIds(): string[]                          { return Array.from(this.entries.keys()); }
  getRefCount(id: string): number               { return this.entries.get(id)?.refCount ?? 0; }

  // ── Release ───────────────────────────────────────────────────────────────

  release(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.refCount--;
    console.log(`[AudioSourceRegistry] release(${id}) — refCount=${entry.refCount}`);
    if (entry.refCount <= 0) this.cleanupEntry(entry);
  }

  forceRelease(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    console.log(`[AudioSourceRegistry] forceRelease(${id})`);
    this.cleanupEntry(entry);
  }

  destroy(): void {
    console.log(`[AudioSourceRegistry] destroy() — ${this.entries.size} source(s)`);
    this.entries.forEach(entry => this.cleanupEntry(entry));
    this.entries.clear();
  }

  // ── Internes ──────────────────────────────────────────────────────────────

  /** <audio> muet : le puits que Chromium exige pour qu'un flux distant alimente Web Audio. */
  private makeAudioSink(stream: MediaStream): HTMLAudioElement {
    const audioEl = document.createElement('audio');
    audioEl.srcObject = stream;
    audioEl.autoplay  = true;
    audioEl.muted     = true;
    audioEl.style.display = 'none';
    document.body.appendChild(audioEl);
    audioEl.play().catch(() => {});
    return audioEl;
  }

  /**
   * Ouvre la source `key`, ou en reprend une référence si elle existe déjà sur ce même
   * flux et ce même contexte : le MediaStreamSource n'est alors PAS recréé.
   */
  private acquireSource(
    key: string, ctx: AudioContext, stream: MediaStream, useAudioEl: boolean, ownsTracks: boolean,
  ): AudioSource {
    const trackId  = stream.getAudioTracks()[0]?.id ?? '';
    // Par clé d'abord ; sinon par piste : le même flux peut être réclamé sous deux clés
    // (micro ouvert par le registre, puis emprunté comme simple flux).
    let existing = this.sources.get(key);
    if (!existing || existing.ctx !== ctx || existing.trackId !== trackId) {
      existing = undefined;
      if (trackId) this.sources.forEach(src => {
        if (!existing && src.ctx === ctx && src.trackId === trackId) existing = src;
      });
    }
    if (existing) {
      existing.refCount++;
      if (useAudioEl && !existing.audioEl) existing.audioEl = this.makeAudioSink(stream);
      console.log(`[AudioSourceRegistry] source ${key} — réutilisée, refCount=${existing.refCount}`);
      return existing;
    }
    const audioEl = useAudioEl ? this.makeAudioSink(stream) : undefined;
    const source: AudioSource = {
      key, ctx, trackId, stream, node: ctx.createMediaStreamSource(stream), refCount: 1, audioEl, ownsTracks,
    };
    this.sources.set(key, source);
    return source;
  }

  /** Rend une référence à la source ; la ferme quand plus personne ne s'en sert. */
  private releaseSource(source: AudioSource): void {
    source.refCount--;
    if (source.refCount > 0) return;
    try { source.node.disconnect(); } catch {}
    if (source.audioEl) {
      try { source.audioEl.pause(); source.audioEl.srcObject = null; source.audioEl.remove(); } catch {}
    }
    if (source.ownsTracks) source.stream.getTracks().forEach(t => t.stop());
    if (this.sources.get(source.key) === source) this.sources.delete(source.key);
  }

  private makeHandle(entry: AudioSourceEntry): AudioSourceHandle {
    let released = false;
    let tap: GainNode | null = null;
    return {
      id:         entry.id,
      // Un RELAIS PRIVÉ, jamais le nœud de la source : celui-ci est partagé par toutes les
      // entrées du même flux, et un porteur de poignée (AudioMixerPipeline) le débranche
      // par un disconnect() sans argument — ce qui couperait toutes les autres entrées.
      // Créé à la première lecture : la plupart des appelants ignorent ce champ.
      get sourceNode(): AudioNode {
        if (!tap) {
          tap = entry.ctx.createGain();
          entry.sourceNode.connect(tap);
          entry.taps.push(tap);
        }
        return tap;
      },
      gainNode:   entry.gainNode,
      ctx:        entry.ctx,
      release: () => {
        if (released) return;
        released = true;
        this.release(entry.id);
      },
    };
  }

  private cleanupEntry(entry: AudioSourceEntry): void {
    console.log(`[AudioSourceRegistry] cleanupEntry(${entry.id})`);
    this.avSyncProbes.get(entry.id)?.stop();
    this.avSyncProbes.delete(entry.id);
    try { entry.outputGainNode.disconnect();   } catch {}
    try { entry.monitorGainNode.disconnect();  } catch {}
    try { entry.postMixNode.disconnect();      } catch {}
    try { entry.dryGain.disconnect();          } catch {}
    try { entry.reverbWetGain.disconnect();    } catch {}
    try { entry.reverbConvolver.disconnect();  } catch {}
    try { entry.delayNode.disconnect();          } catch {}
    try { entry.delayFeedbackGain.disconnect();  } catch {}
    try { entry.delayWetGain.disconnect();       } catch {}
    // Un OscillatorNode continue de tourner indéfiniment tant qu'on ne l'arrête pas
    // explicitement — disconnect() seul ne suffit pas, contrairement aux autres nœuds.
    try { entry.robotOscillator.stop();        } catch {}
    try { entry.robotOscillator.disconnect();  } catch {}
    try { entry.robotRingGain.disconnect();    } catch {}
    try { entry.robotWetGain.disconnect();     } catch {}
    try { entry.chorusLfoA.stop();             } catch {}
    try { entry.chorusLfoA.disconnect();       } catch {}
    try { entry.chorusDepthA.disconnect();     } catch {}
    try { entry.chorusDelayA.disconnect();     } catch {}
    try { entry.chorusLfoB.stop();             } catch {}
    try { entry.chorusLfoB.disconnect();       } catch {}
    try { entry.chorusDepthB.disconnect();     } catch {}
    try { entry.chorusDelayB.disconnect();     } catch {}
    try { entry.chorusWetGain.disconnect();    } catch {}
    if (entry.pitchNode) { try { entry.pitchNode.disconnect(); } catch {} }
    try { entry.merger.disconnect();          } catch {}
    try { entry.analyserL.disconnect();       } catch {}
    try { entry.analyserR.disconnect();       } catch {}
    entry.loudness?.nodes.forEach(n => { try { n.disconnect(); } catch {} });
    try { entry.gainLeft.disconnect();        } catch {}
    try { entry.gainRight.disconnect();       } catch {}
    try { entry.splitter.disconnect();        } catch {}
    try { entry.gainNode.disconnect();        } catch {}
    try { entry.eqLow.disconnect();           } catch {}
    try { entry.eqMid.disconnect();           } catch {}
    try { entry.eqHigh.disconnect();          } catch {}
    try { entry.filterNode.disconnect();      } catch {}
    // Seulement la branche de CETTE entrée : le nœud est partagé, un disconnect() sans
    // argument couperait aussi les autres entrées alimentées par la même source. Le
    // nœud lui-même est fermé par releaseSource quand la dernière entrée part.
    try { entry.sourceNode.disconnect(entry.filterNode); } catch {}
    entry.taps.forEach(t => {
      try { entry.sourceNode.disconnect(t); } catch {}
      try { t.disconnect(); } catch {}
    });
    entry.taps = [];
    this.releaseSource(entry.source);
    this.entries.delete(entry.id);
  }
}